/**
 * THE ONE PLACE THE PLACEHOLDER-VISITOR WIRE KEYS ARE NAMED.
 *
 * ========================= NOT YET VERIFIED ==============================
 * Keys follow the SN-8410 API contract, but nothing here has been exercised
 * against a live response. If they change, edit the string literals in this
 * file and nothing else -- every read and write goes through this module, so
 * no screen spells a wire key itself.
 *
 * To re-point at the real API:
 *   1. Update the key constants below.
 *   2. If the flag's encoding changes, update `isTruthyFlag`.
 *   3. Run `__tests__/placeholderVisitorKeys.test.ts`.
 * =========================================================================
 *
 * Unrelated to `is_placeholder` on a TEAM, which is its own existing feature
 * (a real team record with an id, a roster and a logo). This is a game-level
 * SETTING: there is no team at all, only a name the admin typed. Nothing here
 * should be wired to that flag.
 */

/** Top-level game key flagging a placeholder visitor. */
export const PLACEHOLDER_VISITOR_FLAG_KEY = 'has_placeholder_visitor'

/** Top-level game key holding the admin-typed visitor name. */
export const PLACEHOLDER_VISITOR_NAME_KEY = 'visiting_team_placeholder_name'

/** Request key used when creating/updating a game. */
export const PLACEHOLDER_VISITOR_REQUEST_KEY = 'visiting_team_placeholder_name'

/*
 * `common/reducers/parsers.js` re-spells these into camelCase and does NOT
 * carry the raw keys through, so a PARSED game satisfies neither wire key.
 * Game cards receive a mix of both shapes -- `GameCard` gets the raw API game,
 * `ListRow` gets a parsed one -- so every read below accepts either spelling.
 */
const PLACEHOLDER_VISITOR_FLAG_KEY_PARSED = 'hasPlaceholderVisitor'
const PLACEHOLDER_VISITOR_NAME_KEY_PARSED = 'visitingTeamPlaceholderName'

/** Max length the API accepts for the placeholder name. */
export const PLACEHOLDER_VISITOR_NAME_MAX_LENGTH = 64

/** Minimal shape this module reads. Anything game-like satisfies it. */
export interface PlaceholderVisitorSource {
  visitingTeam?: { id?: string | null } | null
  /** `/home/calendar` spells the relation this way. */
  visiting_team?: { id?: string | null } | null
  /*
   * The four spellings this module reads, declared individually rather than
   * behind `[key: string]: unknown`. An index signature would force every
   * caller's game type to carry one too -- web's typed `Game` does not, so
   * passing it was a type error even though the shape is right.
   */
  has_placeholder_visitor?: unknown
  hasPlaceholderVisitor?: unknown
  visiting_team_placeholder_name?: unknown
  visitingTeamPlaceholderName?: unknown
}

/**
 * Read the flag tolerantly.
 *
 * The contract shows a real boolean, and that is the expected case. Strings
 * are also accepted because a plain truthiness check on one would be wrong in
 * the case that matters: `Boolean('0')` and `Boolean('false')` are both `true`
 * in JS, which would turn every ordinary game into a placeholder one.
 */
const isTruthyFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'number') {
    return value === 1
  }
  if (typeof value !== 'string') {
    return false
  }
  const normalized = value.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

/**
 * The placeholder name on this game, or null when it has none.
 *
 * Trimmed, and an empty string reads as absent -- a whitespace-only value
 * would otherwise render as a blank team name that looks like a load bug.
 */
export const readPlaceholderVisitorName = (
  game: PlaceholderVisitorSource | null | undefined
): string | null => {
  if (!game) {
    return null
  }
  const raw =
    game[PLACEHOLDER_VISITOR_NAME_KEY] ??
    game[PLACEHOLDER_VISITOR_NAME_KEY_PARSED]
  if (typeof raw !== 'string') {
    return null
  }
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Whether this game has a REAL visiting team, in either spelling.
 *
 * Exported because `game.visiting_team` cannot be truth-tested directly:
 * `/home/player-attendance` sends a placeholder game's visitor as `{}` --
 * an EMPTY OBJECT, not null and not absent -- and `{}` is truthy, so
 * `game.visiting_team || resolvePlaceholderVisitorTeam(game)` keeps the `{}`
 * and never reaches the resolver. The card then reads `.name` off it, gets
 * `undefined`, and renders its literal 'Visitor'/'TBD' fallback.
 *
 * The id is what makes a team real: an object with no id has no page to open,
 * no roster and no logo, so there is nothing to render from it.
 */
export const hasRealVisitingTeam = (
  game: PlaceholderVisitorSource | null | undefined
): boolean => Boolean(game?.visitingTeam?.id || game?.visiting_team?.id)

/**
 * Whether this game's visitor is a placeholder rather than a real team.
 *
 * ONLY the explicit flag decides this.
 *
 * The API contract is emphatic that placeholder status must NOT be inferred
 * from a missing `visiting_team_id`: tournament games and some Daysmart games
 * also lack one. Inferring would dress a bracket game -- which has its own
 * `visitingTeamSlot` name -- as a placeholder and hide the slot it should be
 * showing. So a game with a name but no flag is NOT a placeholder game.
 *
 * One guard on top of the flag: a real visiting team wins. If a stale flag
 * ever arrives beside a genuine team, that team has the roster, jersey colors
 * and logo, and rendering a placeholder over it would hide a real matchup.
 *
 * A flagged game with no name is still a placeholder game -- callers render
 * whatever `resolveVisitingTeamDisplay` falls back to rather than a real team
 * that does not exist.
 */
export const hasPlaceholderVisitor = (
  game: PlaceholderVisitorSource | null | undefined
): boolean => {
  //  Both spellings: the API game uses `visitingTeam`, `/home/calendar` uses
  //  `visiting_team`. A real team wins over the flag either way.
  if (!game || hasRealVisitingTeam(game)) {
    return false
  }
  return isTruthyFlag(
    game[PLACEHOLDER_VISITOR_FLAG_KEY] ??
      game[PLACEHOLDER_VISITOR_FLAG_KEY_PARSED]
  )
}
